import React, { useRef, useState, useEffect } from "react";
import { Button, message } from "antd";
import { YhBox } from "@/components/YhBox";
import { YhFormBox } from "@/components/YhFormBox";
import { IYhFormBox, IYhFormItemsBox, FormItemTypeEnum, IYhFormRef } from "@/components/YhFormBox/Types";
import { useCustomerModel } from "../customerModel";
import useNavModel, { RouterParamType } from "@/model/navModel";
import { getFormValidatorErrText } from "@/utils/utils"
import { useDictModel } from "@/views/base/dict/dictModel";
import { DictCodeEnmu } from "@/types/AppDictEnums";
import moment from 'moment';
import useGlobalModel from '@/model/globalModel';

export const BaseCustomerDetails = () => {
    const {
        addFormVal,
        editFormVal,
        userName,
        updateAddFormVal,
        updateEditFormVal,
        insertCustomer,
        editCustomer,
        getCustomerOne
    } = useCustomerModel();
    const { selectedNavId, replaceNav, getSelectedNavState, findNav } = useNavModel(({ selectedNavId }) => [selectedNavId]);
    const { getSelectedDictOne, getTreeSelectOne, getTreeNodeName } = useDictModel(() => []);
    const { isEditable } = useGlobalModel(() => []);
    const formRef = useRef<IYhFormRef>();
    const [detailsInfo] = useState<RouterParamType>(getSelectedNavState() as RouterParamType);

    //编辑时， 没有缓在调用详情接口
    useEffect(() => {
        if (!Object.keys(editFormVal).length && detailsInfo.mode === "edit") {
            getCustomerOne(detailsInfo.id)
        }
        if (Object.keys(addFormVal).length <= 3 && detailsInfo.mode === "add") {
            updateAddFormVal({
                staffName: getSelectedDictOne(DictCodeEnmu.CUSTOMER_MANAGER).find(item => item.label === userName)?.label ?? undefined,
                staffCode: getSelectedDictOne(DictCodeEnmu.CUSTOMER_MANAGER).find(item => item.label === userName)?.value ?? undefined
            })
        }
    }, [detailsInfo]);

    useEffect(() => {
        return () => {
            let addNavExist = findNav("7-2-1");
            let editNavExist = findNav("7-2-2");
            !addNavExist && updateAddFormVal(null);
            !editNavExist && updateEditFormVal(null);
        }
    }, [selectedNavId])

    //关闭当前页
    const onClose = () => {
        replaceNav(selectedNavId, "7-2");
    }

    //重置
    const onReset = () => {
        formRef.current?.resetFields();
        if (detailsInfo.mode === "edit") {
            getCustomerOne(detailsInfo.id)
        } else {
            updateAddFormVal(null);
        }
    };

    //表单提交
    const onSubmit = async () => {
        formRef.current?.validateFields().then(val => {
            let data = {
                ...val,
                name: val.name.trim(),
                contractStartTime: val.contractStartTime ? moment(val.contractStartTime).format("YYYY-MM-DD HH:mm:ss") : '',
                contractEndTime: val.contractEndTime ? moment(val.contractEndTime).format("YYYY-MM-DD HH:mm:ss") : '',
                categoryName: getTreeNodeName(val.categoryCode) ?? '',
                staffName: getTreeNodeName(val.staffCode) ?? '',
                countryName: getTreeNodeName(val.countryCode) ?? '',
                qualifications: val.qualifications ? JSON.stringify(val.qualifications) : ""
            }
            if (detailsInfo.mode === "edit") {
                editCustomer({ ...data, id: detailsInfo.id }).then(res => {
                    res && onClose()
                    updateEditFormVal(null);
                })
            } else {
                insertCustomer(data).then(res => {
                    res && onClose()
                    updateAddFormVal(null);
                })
            }
        }).catch(err => {
            message.warning(getFormValidatorErrText(err));
        })
    }

    //实时保留缓存
    const onChange = (changedValues: { [key: string]: any }) => {
        detailsInfo.mode === "edit" ? updateEditFormVal(changedValues) : updateAddFormVal(changedValues);
    }

    //图片变更缓存保留  保留缓存及回显表单
    const onSetFieldsValue = (formValues: { [key: string]: any }) => {
        detailsInfo.mode === "edit" ? updateEditFormVal(formValues) : updateAddFormVal(formValues);
    }


    const baseFormItem: IYhFormItemsBox = {
        title: "基础信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户编码",
                fieldName: "code",
                disable: true,
                placeholder: "自动生成"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户名称",
                fieldName: "name",
                rules: [{ required: true, message: "请输入客户名称" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系人",
                fieldName: "contact",
                rules: [{ required: true, message: "请输入联系人" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系电话",
                fieldName: "phone",
                rules: [{ required: true, message: "请输入联系电话" }]
            }, {
                type: FormItemTypeEnum.TreeSelect,
                fieldLabel: "客户类型",
                fieldName: "categoryCode",
                treeData: getTreeSelectOne(DictCodeEnmu.CUSTOMER_TYPE),
                rules: [{ required: true, message: "请选择客户类型" }]
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "性别",
                fieldName: "sex",
                options: [{ label: "男", value: "1" }, { label: "女", value: "0" }],
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "办公电话",
                fieldName: "tel"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "联系地址",
                fieldName: "address",
                rules: [{ required: true, message: "请输入联系地址" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户职务",
                fieldName: "job"
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "客户负责人",
                fieldName: "staffCode",
                options: getSelectedDictOne(DictCodeEnmu.CUSTOMER_MANAGER),
            }, {
                type: FormItemTypeEnum.Select,
                fieldLabel: "贸易国别",
                fieldName: "countryCode",
                options: getSelectedDictOne(DictCodeEnmu.COUNTRY),
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户编码生成",
                fieldName: "abbreviation",
                rules: [{ required: true, message: "请输入客户编码生成" }]
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "所属门店",
                fieldName: "shopName",
                disable: true,
                hidden: true
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "备注",
                fieldName: "note",
            },
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户来源",
                fieldName: "source"
            },
            {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "合同签订日期",
                fieldName: "contractStartTime",
                format: "YYYY-MM-DD HH:mm:ss",
            },
            {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "合同到期日期",
                fieldName: "contractEndTime",
                format: "YYYY-MM-DD HH:mm:ss",
            },
            {
                type: FormItemTypeEnum.Switch,
                fieldLabel: "客户状态",
                fieldName: "status"
            },
            //带出字段
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户类型名称",
                fieldName: "categoryName",
                hidden: true
            },
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "客户负责人名称",
                fieldName: "staffName",
                hidden: true
            },
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "贸易国别",
                fieldName: "countryName",
                hidden: true
            },
        ]
    };
    const priceFormItem: IYhFormItemsBox = {
        title: "开票信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "公司名称",
                fieldName: "invoiceTitle",
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "开户行地址",
                fieldName: "bankAddress"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "银行账户",
                fieldName: "bankAcct"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "邮寄地址",
                fieldName: "postAddress"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "公司注册地址",
                fieldName: "registeredAddress"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "电话",
                fieldName: "bankPhone"
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "统一社会信用代码",
                fieldName: "licenseNo"
            },
        ]
    }


    // 资质文件
    const fileFormItem: IYhFormItemsBox = {
        title: '资质信息',
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.FileImage,
                fieldLabel: "资质文件",
                fieldName: "qualifications",
                colClassName: "file-image-box ant-col-h-auto",
                span: 24,
                md5List: detailsInfo.mode === "edit" ? editFormVal.qualifications ?? [] : addFormVal.qualifications ?? [],
                onSetFieldsValue
            }
        ]
    }

    const otherFormItem: IYhFormItemsBox = {
        title: "其它信息",
        style: { padding: "18px 77px 0 0", marginBottom: "10px" },
        items: [
            {
                type: FormItemTypeEnum.Input,
                fieldLabel: "创建人",
                fieldName: "createUser",
                disable: true
            }, {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "创建日期",
                fieldName: "createTime",
                disable: isEditable
            }, {
                type: FormItemTypeEnum.Input,
                fieldLabel: "修改人",
                fieldName: "updateUser",
                disable: true
            }, {
                type: FormItemTypeEnum.Datepicker,
                fieldLabel: "修改日期",
                fieldName: "updateTime",
                disable: isEditable
            }
        ]
    }

    const supplierFromConfig: IYhFormBox<any> = {
        formRef,
        labelCol: { span: 8 },
        labelAlign: "right",
        formValues: detailsInfo.mode === "edit" ? editFormVal : addFormVal,
        onChange,
        boxs: [baseFormItem, priceFormItem, fileFormItem, otherFormItem]
    }


    const action = <>
        {/* <YhAuth resCode={detailsInfo.mode === "edit" ? ResourceAuthEnum.DATA_C_ED_SAVE : ResourceAuthEnum.DATA_C_AD_SAVE}> */}
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onSubmit}
        >保存</Button>
        {/* </YhAuth> */}
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onReset}
        >重置</Button>
        <Button
            className="mr-5"
            type="link"
            shape="round"
            onClick={onClose}
        >取消</Button>
    </>;

    return (
        <>
            <YhBox title={`客户${detailsInfo.mode === "edit" ? "编辑" : "新增"}`} action={action} style={{ paddingBottom: "0px", marginBottom: "10px" }}></YhBox>
            <YhFormBox {...supplierFromConfig} />
        </>
    )
}